/**
 * TemplateTokenInput
 *
 * Contenteditable text input that renders inline `{token}` chips and shows an
 * autocomplete dropdown only while the user is actively typing a fragment that
 * matches one of the provided suggestions. Selecting a suggestion replaces the
 * matched fragment with a styled, non-editable token.
 *
 * Storage shape stays a plain string with `{token.path}` placeholders so callers
 * can keep persisting it as a flat value.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/shared/lib/utils'

const TOKEN_PATTERN = /\{([^}]+)\}/g

export type TemplateTokenSuggestion = {
  value: string
  label: string
  description?: string
}

type Segment =
  | { type: 'text'; value: string }
  | { type: 'token'; value: string }

type ActiveQuery = {
  query: string
  start: number
  end: number
}

function getTokenString(value: string) {
  return `{${value}}`
}

function parseValue(value: string): Segment[] {
  const segments: Segment[] = []
  let cursor = 0
  for (const match of value.matchAll(TOKEN_PATTERN)) {
    const matchIndex = match.index ?? 0
    if (matchIndex > cursor) {
      segments.push({ type: 'text', value: value.slice(cursor, matchIndex) })
    }
    segments.push({ type: 'token', value: match[1] })
    cursor = matchIndex + match[0].length
  }
  if (cursor < value.length) {
    segments.push({ type: 'text', value: value.slice(cursor) })
  }
  return segments
}

function isTokenNode(node: Node | null): node is HTMLElement {
  return (
    node instanceof HTMLElement &&
    node.dataset.templateToken === 'true'
  )
}

function serializeEditorValue(root: HTMLElement): string {
  return Array.from(root.childNodes)
    .map((node) => {
      if (isTokenNode(node)) {
        return getTokenString(node.dataset.tokenValue ?? '')
      }
      return node.textContent ?? ''
    })
    .join('')
}

function serializeFragmentValue(fragment: DocumentFragment): string {
  const wrapper = document.createElement('div')
  wrapper.appendChild(fragment)
  return serializeEditorValue(wrapper)
}

function getCaretOffset(root: HTMLElement): number {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) {
    return serializeEditorValue(root).length
  }
  const range = selection.getRangeAt(0)
  if (!root.contains(range.endContainer)) {
    return serializeEditorValue(root).length
  }
  const preRange = range.cloneRange()
  preRange.selectNodeContents(root)
  preRange.setEnd(range.endContainer, range.endOffset)
  const fragment = preRange.cloneContents()
  return serializeFragmentValue(fragment).length
}

function setCaretOffset(root: HTMLElement, targetOffset: number) {
  const selection = window.getSelection()
  if (!selection) return

  const range = document.createRange()
  let remaining = Math.max(targetOffset, 0)

  for (const node of Array.from(root.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      const textLength = node.textContent?.length ?? 0
      if (remaining <= textLength) {
        range.setStart(node, remaining)
        range.collapse(true)
        selection.removeAllRanges()
        selection.addRange(range)
        return
      }
      remaining -= textLength
      continue
    }
    if (isTokenNode(node)) {
      const tokenLength = getTokenString(node.dataset.tokenValue ?? '').length
      if (remaining <= tokenLength) {
        if (remaining === 0) {
          range.setStartBefore(node)
        } else {
          range.setStartAfter(node)
        }
        range.collapse(true)
        selection.removeAllRanges()
        selection.addRange(range)
        return
      }
      remaining -= tokenLength
    }
  }

  range.selectNodeContents(root)
  range.collapse(false)
  selection.removeAllRanges()
  selection.addRange(range)
}

function getEditorValueLength(root: HTMLElement) {
  return serializeEditorValue(root).length
}

function getSelectionRect(root: HTMLElement): DOMRect {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) {
    return root.getBoundingClientRect()
  }
  const range = selection.getRangeAt(0)
  if (!root.contains(range.endContainer)) {
    return root.getBoundingClientRect()
  }
  const rect =
    range.getClientRects()[0] ?? range.cloneRange().getBoundingClientRect()
  if (rect.width || rect.height) return rect
  return root.getBoundingClientRect()
}

function getAdjacentToken(
  root: HTMLElement,
  direction: 'backward' | 'forward',
): HTMLElement | null {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) {
    return null
  }
  const range = selection.getRangeAt(0)
  if (!root.contains(range.startContainer)) return null

  if (range.startContainer.nodeType === Node.TEXT_NODE) {
    const textNode = range.startContainer
    const textLength = textNode.textContent?.length ?? 0
    if (direction === 'backward' && range.startOffset === 0) {
      return isTokenNode(textNode.previousSibling)
        ? (textNode.previousSibling as HTMLElement)
        : null
    }
    if (direction === 'forward' && range.startOffset === textLength) {
      return isTokenNode(textNode.nextSibling)
        ? (textNode.nextSibling as HTMLElement)
        : null
    }
  }

  if (range.startContainer === root) {
    const adjacentIndex =
      direction === 'backward' ? range.startOffset - 1 : range.startOffset
    const adjacent = root.childNodes[adjacentIndex] ?? null
    return isTokenNode(adjacent) ? adjacent : null
  }

  if (
    range.startContainer instanceof HTMLElement &&
    isTokenNode(range.startContainer)
  ) {
    return range.startContainer
  }

  return null
}

function getSelectedToken(root: HTMLElement): HTMLElement | null {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) {
    return null
  }

  const range = selection.getRangeAt(0)
  if (!root.contains(range.startContainer)) return null

  if (isTokenNode(range.startContainer)) {
    return range.startContainer
  }

  if (
    range.startContainer instanceof HTMLElement &&
    isTokenNode(range.startContainer.parentElement)
  ) {
    return range.startContainer.parentElement
  }

  if (
    range.startContainer.nodeType === Node.TEXT_NODE &&
    range.startContainer.parentElement &&
    isTokenNode(range.startContainer.parentElement)
  ) {
    return range.startContainer.parentElement
  }

  return null
}

function getTokenOffsets(root: HTMLElement, tokenNode: HTMLElement) {
  let offset = 0

  for (const node of Array.from(root.childNodes)) {
    if (node === tokenNode) {
      const tokenLength = getTokenString(tokenNode.dataset.tokenValue ?? '').length
      return {
        start: offset,
        end: offset + tokenLength,
      }
    }

    if (node.nodeType === Node.TEXT_NODE) {
      offset += node.textContent?.length ?? 0
      continue
    }

    if (isTokenNode(node)) {
      offset += getTokenString(node.dataset.tokenValue ?? '').length
    }
  }

  return null
}

function getActiveQuery(
  value: string,
  caretOffset: number,
  minSearchLength: number,
): ActiveQuery | null {
  const beforeCaret = value.slice(0, caretOffset)
  const queryMatch = beforeCaret.match(/([^{}\s]+)$/)
  if (!queryMatch) return null
  const query = queryMatch[1]
  const queryStart = beforeCaret.length - query.length
  const previousChar = beforeCaret[queryStart - 1]
  if (previousChar && !/[\s(,;[{]/.test(previousChar)) return null
  if (query.length < minSearchLength) return null
  const start = previousChar === '{' ? queryStart - 1 : queryStart
  return { query, start, end: caretOffset }
}

function renderEditorValue(
  root: HTMLElement,
  value: string,
  optionLookup: Record<string, TemplateTokenSuggestion>,
) {
  root.replaceChildren()
  for (const segment of parseValue(value)) {
    if (segment.type === 'text') {
      root.appendChild(document.createTextNode(segment.value))
      continue
    }
    const option = optionLookup[segment.value]
    const token = document.createElement('span')
    token.dataset.templateToken = 'true'
    token.dataset.tokenValue = segment.value
    token.contentEditable = 'false'
    const known = Boolean(option)
    token.className = cn(
      'mx-0.5 inline-flex items-center rounded-[4px] border-[0.5px] px-1.5 py-px align-baseline font-mono text-[11px] leading-[18px]',
      known
        ? 'border-[var(--rule-strong)] bg-[var(--bg-subtle)] text-[var(--ink)]'
        : 'border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)]',
    )
    token.textContent = option?.label ?? segment.value
    root.appendChild(token)
  }
}

function buildOptionLookup(
  options: TemplateTokenSuggestion[],
): Record<string, TemplateTokenSuggestion> {
  return options.reduce<Record<string, TemplateTokenSuggestion>>(
    (acc, option) => {
      if (option.value) acc[option.value] = option
      return acc
    },
    {},
  )
}

function filterSuggestions(
  source: TemplateTokenSuggestion[],
  query: string,
): TemplateTokenSuggestion[] {
  const normalised = query.trim().toLowerCase()
  if (!normalised.length) return []
  return source.filter((option) => {
    const haystack = `${option.label} ${option.value} ${option.description ?? ''}`
      .toLowerCase()
    return haystack.includes(normalised)
  })
}

export type TemplateTokenInputProps = {
  value: string
  onChange: (value: string) => void
  suggestions: TemplateTokenSuggestion[]
  placeholder?: string
  minSearchLength?: number
  disabled?: boolean
  className?: string
  ariaLabel?: string
}

export function TemplateTokenInput({
  value,
  onChange,
  suggestions,
  placeholder,
  minSearchLength = 1,
  disabled,
  className,
  ariaLabel,
}: TemplateTokenInputProps): ReactNode {
  const editorRef = useRef<HTMLDivElement | null>(null)
  const blurTimeoutRef = useRef<number | null>(null)
  const pendingCaretOffsetRef = useRef<number | null>(null)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })
  const [activeQuery, setActiveQuery] = useState<ActiveQuery | null>(null)
  const [isFocused, setIsFocused] = useState(false)

  const optionLookup = useMemo(
    () => buildOptionLookup(suggestions),
    [suggestions],
  )

  const visibleSuggestions = useMemo(() => {
    if (!activeQuery) return []
    return filterSuggestions(suggestions, activeQuery.query).slice(0, 8)
  }, [activeQuery, suggestions])

  const highlightedSuggestion =
    visibleSuggestions[highlightedIndex] ?? visibleSuggestions[0] ?? null

  const updateMenuPosition = useCallback(() => {
    const root = editorRef.current
    if (!root) return
    const rect = getSelectionRect(root)
    setMenuPosition({ top: rect.bottom + 6, left: rect.left })
  }, [])

  const syncSuggestionState = useCallback(() => {
    const root = editorRef.current
    if (!root || document.activeElement !== root) {
      setActiveQuery(null)
      return
    }
    const nextValue = serializeEditorValue(root)
    const caretOffset = getCaretOffset(root)
    const nextQuery = getActiveQuery(nextValue, caretOffset, minSearchLength)
    if (!nextQuery) {
      setActiveQuery(null)
      return
    }
    if (filterSuggestions(suggestions, nextQuery.query).length < 1) {
      setActiveQuery(null)
      return
    }
    setActiveQuery((current) => {
      if (
        current &&
        current.query === nextQuery.query &&
        current.start === nextQuery.start &&
        current.end === nextQuery.end
      ) {
        return current
      }
      setHighlightedIndex(0)
      return nextQuery
    })
    updateMenuPosition()
  }, [minSearchLength, suggestions, updateMenuPosition])

  function commitValue(nextValue: string) {
    onChange(nextValue)
  }

  function handleInput() {
    const root = editorRef.current
    if (!root) return
    commitValue(serializeEditorValue(root))
    syncSuggestionState()
  }

  function handleSelectSuggestion(option: TemplateTokenSuggestion) {
    const root = editorRef.current
    if (!root || !activeQuery) return
    const replacement = getTokenString(option.value)
    const currentValue = serializeEditorValue(root)
    const nextValue =
      currentValue.slice(0, activeQuery.start) +
      replacement +
      currentValue.slice(activeQuery.end)
    pendingCaretOffsetRef.current = activeQuery.start + replacement.length
    setActiveQuery(null)
    commitValue(nextValue)
    window.requestAnimationFrame(() => editorRef.current?.focus())
  }

  function removeAdjacentToken(direction: 'backward' | 'forward') {
    const root = editorRef.current
    if (!root) return false
    const tokenNode = getAdjacentToken(root, direction)
    if (!tokenNode) return false
    const tokenString = getTokenString(tokenNode.dataset.tokenValue ?? '')
    const currentValue = serializeEditorValue(root)
    const caretOffset = getCaretOffset(root)
    const tokenStart =
      direction === 'backward'
        ? caretOffset - tokenString.length
        : caretOffset
    const nextValue =
      currentValue.slice(0, tokenStart) +
      currentValue.slice(tokenStart + tokenString.length)
    pendingCaretOffsetRef.current = tokenStart
    commitValue(nextValue)
    setActiveQuery(null)
    return true
  }

  function removeSelectedToken() {
    const root = editorRef.current
    if (!root) return false

    const tokenNode = getSelectedToken(root)
    if (!tokenNode) return false

    const offsets = getTokenOffsets(root, tokenNode)
    if (!offsets) return false

    const currentValue = serializeEditorValue(root)
    const nextValue = currentValue.slice(0, offsets.start) + currentValue.slice(offsets.end)

    pendingCaretOffsetRef.current = offsets.start
    commitValue(nextValue)
    setActiveQuery(null)
    return true
  }

  function insertSpaceAfterSelectedToken() {
    const root = editorRef.current
    if (!root) return false

    const tokenNode = getSelectedToken(root)
    if (!tokenNode) return false

    const offsets = getTokenOffsets(root, tokenNode)
    if (!offsets) return false

    const currentValue = serializeEditorValue(root)
    const nextValue =
      currentValue.slice(0, offsets.end) + ' ' + currentValue.slice(offsets.end)

    pendingCaretOffsetRef.current = offsets.end + 1
    commitValue(nextValue)
    setActiveQuery(null)
    return true
  }

  useEffect(() => {
    const root = editorRef.current
    if (!root) return
    const isEditorFocused = document.activeElement === root
    const currentCaret = isEditorFocused ? getCaretOffset(root) : null
    renderEditorValue(root, value, optionLookup)
    if (isEditorFocused) {
      setCaretOffset(
        root,
        pendingCaretOffsetRef.current ?? currentCaret ?? value.length,
      )
    }
    pendingCaretOffsetRef.current = null
  }, [value, optionLookup])

  useEffect(() => {
    if (!isFocused) return
    function onSelectionChange() {
      syncSuggestionState()
    }
    function onWindowChange() {
      if (activeQuery) updateMenuPosition()
    }
    document.addEventListener('selectionchange', onSelectionChange)
    window.addEventListener('resize', onWindowChange)
    window.addEventListener('scroll', onWindowChange, true)
    return () => {
      document.removeEventListener('selectionchange', onSelectionChange)
      window.removeEventListener('resize', onWindowChange)
      window.removeEventListener('scroll', onWindowChange, true)
    }
  }, [activeQuery, isFocused, syncSuggestionState, updateMenuPosition])

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) window.clearTimeout(blurTimeoutRef.current)
    }
  }, [])

  const showPlaceholder = !value.length && Boolean(placeholder)

  return (
    <div className="relative">
      {showPlaceholder ? (
        <div className="pointer-events-none absolute left-3 top-2 text-[12px] text-[var(--ink-3)]">
          {placeholder}
        </div>
      ) : null}
      <div
        ref={editorRef}
        role="textbox"
        aria-label={ariaLabel}
        aria-multiline="false"
        contentEditable={!disabled}
        suppressContentEditableWarning
        className={cn(
          'min-h-9 w-full whitespace-pre-wrap break-words rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-1.5 font-mono text-[13px] leading-6 text-[var(--ink)] outline-none transition-colors',
          'focus:border-[var(--rule-strong)]',
          disabled && 'cursor-not-allowed opacity-60',
          className,
        )}
        onInput={handleInput}
        onFocus={() => {
          setIsFocused(true)
          window.requestAnimationFrame(() => {
            const root = editorRef.current
            if (!root) return
            const selection = window.getSelection()
            const hasSelectionInside =
              selection &&
              selection.rangeCount > 0 &&
              root.contains(selection.getRangeAt(0).startContainer)
            if (!hasSelectionInside) {
              setCaretOffset(root, getEditorValueLength(root))
            }
            syncSuggestionState()
          })
        }}
        onBlur={() => {
          blurTimeoutRef.current = window.setTimeout(() => {
            setIsFocused(false)
            setActiveQuery(null)
          }, 120)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Backspace' && removeSelectedToken()) {
            event.preventDefault()
            return
          }
          if (event.key === ' ' && insertSpaceAfterSelectedToken()) {
            event.preventDefault()
            return
          }
          if (event.key === 'Backspace' && removeAdjacentToken('backward')) {
            event.preventDefault()
            return
          }
          if (event.key === 'Delete' && removeAdjacentToken('forward')) {
            event.preventDefault()
            return
          }
          if (event.key === 'Enter') {
            if (activeQuery && highlightedSuggestion) {
              event.preventDefault()
              handleSelectSuggestion(highlightedSuggestion)
              return
            }
            event.preventDefault()
            return
          }
          if (!activeQuery || visibleSuggestions.length < 1) return
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            setHighlightedIndex(
              (prev) => (prev + 1) % visibleSuggestions.length,
            )
            return
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault()
            setHighlightedIndex(
              (prev) =>
                (prev - 1 + visibleSuggestions.length) %
                visibleSuggestions.length,
            )
            return
          }
          if (event.key === 'Tab') {
            event.preventDefault()
            if (highlightedSuggestion) handleSelectSuggestion(highlightedSuggestion)
            return
          }
          if (event.key === 'Escape') {
            setActiveQuery(null)
          }
        }}
        onKeyUp={syncSuggestionState}
        onClick={syncSuggestionState}
      />
      {typeof document !== 'undefined' &&
        isFocused &&
        activeQuery &&
        visibleSuggestions.length > 0 &&
        createPortal(
          <div
            role="listbox"
            className="fixed z-[1000] min-w-[220px] max-w-[360px] overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] shadow-lg"
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            {visibleSuggestions.map((option, index) => {
              const isActive = index === highlightedIndex
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={cn(
                    'flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition-colors',
                    isActive
                      ? 'bg-[var(--bg-subtle)]'
                      : 'hover:bg-[var(--bg-subtle)]',
                  )}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    if (blurTimeoutRef.current) {
                      window.clearTimeout(blurTimeoutRef.current)
                    }
                    handleSelectSuggestion(option)
                  }}
                >
                  <span className="truncate text-[12px] text-[var(--ink)]">
                    {option.label}
                  </span>
                  <span className="truncate font-mono text-[10px] text-[var(--ink-3)]">
                    {getTokenString(option.value)}
                  </span>
                </button>
              )
            })}
          </div>,
          document.body,
        )}
    </div>
  )
}
